import { useEffect, useState } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { TierBadge } from "@/pages/Leaderboard";
import { currency, countryFlag, fmtDate } from "@/lib/format";
import { Plus, Mail, Phone, X } from "lucide-react";
import { toast } from "sonner";

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/partners"); setPartners(data); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="partners-page">
      <PageHeader
        eyebrow="Network"
        title="Referral partners"
        subtitle="Agencies and individuals who refer international patients to AIMS."
        actions={
          <button onClick={() => setShowNew(true)} data-testid="new-partner-button"
            className="bg-[#2A4D3E] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1E3B2F] flex items-center gap-2">
            <Plus size={15} /> New partner
          </button>
        }
      />
      {loading ? <div className="text-sm text-[#5C6660]">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="partners-grid">
          {partners.map((p) => (
            <div key={p.id} className="bg-white border border-[#E8E6E1] rounded-2xl p-6 hover:shadow-md transition" data-testid={`partner-${p.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#E6F0EB] text-[#2A4D3E] flex items-center justify-center font-semibold">
                    {p.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-[#2A332C]">{p.name}</div>
                    <div className="text-[11px] text-[#8E9992]">{p.organization}</div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <div className="text-xs text-[#5C6660]">{countryFlag(p.country)} {p.country}</div>
                  {p.tier && <TierBadge tier={p.tier} />}
                </div>
              </div>
              <div className="space-y-1.5 text-xs text-[#5C6660] mb-4">
                <div className="flex items-center gap-1.5"><Mail size={12} /> {p.email}</div>
                {p.phone && <div className="flex items-center gap-1.5"><Phone size={12} /> {p.phone}</div>}
              </div>
              <div className="grid grid-cols-3 gap-3 pt-4 border-t border-[#E8E6E1]">
                <Stat label="Leads" value={p.lead_count || 0} />
                <Stat label="Patients" value={p.patient_count || 0} />
                <Stat label="Earned" value={currency(p.total_commission || 0)} />
              </div>
              <div className="mt-3 text-[11px] text-[#8E9992]">
                Commission rate: <span className="font-semibold text-[#2A332C]">{((p.commission_rate || 0) * 100).toFixed(0)}%</span>
                {p.pending_commission > 0 && (
                  <span className="ml-2 text-[#B26B00]">· {currency(p.pending_commission)} pending</span>
                )}
              </div>
            </div>
          ))}
          {partners.length === 0 && <div className="col-span-full text-center text-sm text-[#8E9992] py-12">No partners onboarded yet.</div>}
        </div>
      )}
      {showNew && <NewPartnerModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#8E9992] font-semibold">{label}</div>
      <div className="text-sm font-semibold text-[#2A332C]">{value}</div>
    </div>
  );
}

function NewPartnerModal({ onClose, onCreated }) {
  // Generate a random temporary password each time the modal opens so we never
  // ship a hardcoded default. Admin can override before creating.
  const [form, setForm] = useState(() => ({
    name: "", email: "",
    password: `Aims-${Math.random().toString(36).slice(2, 8)}-${Math.random().toString(36).slice(2, 6)}`,
    organization: "",
    country: "", phone: "", role: "partner",
  }));
  const [submitting, setSubmitting] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/users", form);
      toast.success("Partner created");
      onCreated();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <h2 className="font-heading text-2xl font-semibold text-[#2A332C]">New partner</h2>
          <button onClick={onClose}><X size={20} className="text-[#8E9992]" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <ModalField label="Full name" name="name" value={form.name} onChange={onChange} required />
          <ModalField label="Email" type="email" name="email" value={form.email} onChange={onChange} required />
          <ModalField label="Initial password" name="password" value={form.password} onChange={onChange} required />
          <ModalField label="Organization" name="organization" value={form.organization} onChange={onChange} />
          <div className="grid grid-cols-2 gap-3">
            <ModalField label="Country" name="country" value={form.country} onChange={onChange} />
            <ModalField label="Phone" name="phone" value={form.phone} onChange={onChange} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-[#5C6660]">Cancel</button>
            <button type="submit" disabled={submitting} className="bg-[#2A4D3E] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#1E3B2F]">
              {submitting ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalField({ label, ...rest }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#5C6660] mb-1.5">{label}</label>
      <input {...rest} className="w-full border border-[#E8E6E1] rounded-xl px-4 py-2.5 bg-[#FDFCFB] text-[#2A332C] focus:outline-none focus:border-[#2A4D3E] focus:ring-1 focus:ring-[#2A4D3E]" />
    </div>
  );
}
